"use client";

import { useDashboardPathname as usePathname } from "@/hooks/useDashboardNavigation";
import { dateRangeAtom } from "@/hooks/useConnectedGithubOrgs";
import {
  dateRangeUserTouchedAtom,
  recentDateRangeAtom,
  recentTimeTabAtom,
  tabAtom,
} from "@/atoms/runsFilterAtoms";
import { labelsToDateRanges } from "@/lib/utils";
import { useSetAtom, useStore } from "jotai";
import { useDashboardAppRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";

/**
 * Default tab per track. When the atom is at the default and the URL is empty,
 * we leave the URL empty (no need to clutter it with the default).
 */
const DEFAULT_TAB_BY_SOURCE = {
  analytics: "MTD",
  recent_range: "1D",
} as const;

/**
 * Returns namespaced URL param keys for date range persistence.
 *
 * Analytics pages use `period`/`from`/`to`. Recent-range pages (Runs, Logs)
 * use the `recent_`-prefixed variants. The two tracks share `tabAtom` vs
 * `recentTimeTabAtom` and `dateRangeAtom` vs `recentDateRangeAtom`, so
 * namespaced URL keys keep them from cross-contaminating each other on the
 * same URL.
 */
function getParamKeys(isSourceRecent: boolean) {
  const prefix = isSourceRecent ? "recent_" : "";
  return {
    period: `${prefix}period`,
    from: `${prefix}from`,
    to: `${prefix}to`,
  };
}

/**
 * Syncs the date range tab selection and custom date ranges to/from URL query
 * params.
 *
 * Two URL shapes, in the style of Datadog's time picker:
 *
 * - Live (`?period=7D`): the recipient recomputes the window relative to their
 *   own "now". Used for preset tab clicks like "7D" or "MTD". Presence of
 *   `period` (and absence of `from`/`to`) implies live mode.
 *
 * - Frozen (`?from=...&to=...`): URL carries absolute ISO timestamps and the
 *   recipient sees the exact same window. Used for custom date picks, chart
 *   drag-to-zoom, and any non-preset dynamic label. The tab is implicitly
 *   "custom" on the recipient's side.
 *
 * Callers control the mode by whether they pass a `dateRange` to
 * `updateDateRangeUrl`: omitting it for a known preset writes live, passing
 * one writes frozen.
 *
 * When `hydrate: true`, on mount the hook reconciles the URL and the atoms in
 * both directions:
 *
 * - If the URL has params, the atoms are set to match.
 * - Else, if the atoms are non-default (the user picked a window on a previous
 *   page and navigated here), the URL is written from the atoms so this page
 *   produces a shareable link.
 */
export function useDateRangeQueryParams(options?: {
  source?: string;
  hydrate?: boolean;
  /**
   * Overrides the per-track default tab (MTD for analytics, 1D for recent).
   * A page passes this when its data is better served by a different default
   * window, e.g. the repository cache page defaults to a rolling 30D so the
   * charts show a full window on the first of the month instead of a single
   * MTD day. When set and the URL has no date params, the atoms are seeded to
   * this tab and the URL is written so the choice is shareable.
   */
  defaultTab?: string;
}) {
  const {
    source,
    hydrate = false,
    defaultTab: defaultTabOverride,
  } = options ?? {};
  const isSourceRecent = source === "recent_range";
  const paramKeys = useMemo(
    () => getParamKeys(isSourceRecent),
    [isSourceRecent],
  );
  const trackDefaultTab =
    DEFAULT_TAB_BY_SOURCE[isSourceRecent ? "recent_range" : "analytics"];
  const defaultTab = defaultTabOverride ?? trackDefaultTab;

  const setDateRange = useSetAtom(
    isSourceRecent ? recentDateRangeAtom : dateRangeAtom,
  );
  const setTab = useSetAtom(isSourceRecent ? recentTimeTabAtom : tabAtom);
  // Use the Jotai store for non-reactive reads inside the mount effect. We
  // don't want to subscribe to atom changes here: non-hydrating consumers
  // (e.g. DailyMetricsChart) would otherwise pay for re-renders they don't
  // need.
  const store = useStore();

  const pathname = usePathname();
  const { replace } = useRouter();

  // Guard against circular updates during URL -> atom hydration
  const isHydratingRef = useRef(false);

  // Seed the page's default window synchronously, before the first render's
  // children read the date-range atoms. The mount effect that reconciles the
  // URL runs *after* paint, but suspense consumers on the same page (e.g. the
  // repository cache charts via useRepoCacheSummary) read startDate/endDate
  // during the first render. If we seeded only in the effect, that first render
  // would fetch against the stale track default (MTD, often a single day at the
  // start of a month) and then refetch once the effect applied the override,
  // flashing a wrong/empty chart and doubling the request. Seeding here keeps
  // the very first fetch on the correct window.
  //
  // Runs at most once (lazy initializer). Only applies when this page
  // overrides the track default, the URL carries no date params, and the user
  // hasn't explicitly picked a window this session (see the effect for why the
  // `dateRangeUserTouchedAtom` gate, not the atom value, is authoritative).
  // The mount effect reads the result to know it only needs to write the URL
  // (the atoms are already seeded), and to skip the URL-hydration and
  // sibling-atom-passthrough branches.
  const [seededDefaultOverride] = useState(() => {
    if (!hydrate || !defaultTabOverride || typeof window === "undefined") {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    const hasUrlDateParams =
      !!params.get(paramKeys.from) ||
      !!params.get(paramKeys.to) ||
      !!params.get(paramKeys.period);
    const userTouchedDateRange = store.get(dateRangeUserTouchedAtom);
    const overrideRange = labelsToDateRanges()[defaultTabOverride];
    if (hasUrlDateParams || userTouchedDateRange || !overrideRange) {
      // Nothing to seed; let the mount effect handle URL hydration / passthrough.
      return false;
    }
    store.set(isSourceRecent ? recentTimeTabAtom : tabAtom, defaultTabOverride);
    store.set(isSourceRecent ? recentDateRangeAtom : dateRangeAtom, {
      ...overrideRange,
    });
    return true;
  });

  const updateDateRangeUrl = useCallback(
    (tab: string, dateRange?: DateRange) => {
      if (isHydratingRef.current) return;

      // Read current params from window.location to avoid stale searchParams
      // that can cause other filter query params to be dropped.
      const params = new URLSearchParams(window.location.search);
      const labelToDateRange = labelsToDateRanges();
      const hasExplicitRange = !!(dateRange?.from && dateRange?.to);

      if (hasExplicitRange) {
        // Frozen: pin the exact window so the recipient sees the same range.
        // `period` is omitted; the recipient infers `tab="custom"` from the
        // presence of from/to.
        params.set(paramKeys.from, dateRange.from!.toISOString());
        params.set(paramKeys.to, dateRange.to!.toISOString());
        params.delete(paramKeys.period);
      } else if (labelToDateRange[tab]) {
        // Live: known preset, no explicit range given. Recipient recomputes
        // relative to their own "now".
        params.set(paramKeys.period, tab);
        params.delete(paramKeys.from);
        params.delete(paramKeys.to);
      } else {
        // Unknown tab with no range: clear everything.
        params.delete(paramKeys.period);
        params.delete(paramKeys.from);
        params.delete(paramKeys.to);
      }

      const search = params.toString();
      replace(search ? `${pathname}?${search}` : pathname);
    },
    [pathname, replace, paramKeys],
  );

  // On mount, reconcile URL and atoms once.
  useEffect(() => {
    if (!hydrate) return;

    // The default-window override was already applied to the atoms during
    // render (so the first fetch used the right window). All that's left is to
    // write the live URL so the choice is shareable.
    if (seededDefaultOverride) {
      updateDateRangeUrl(defaultTabOverride!);
      isHydratingRef.current = true;
      requestAnimationFrame(() => {
        isHydratingRef.current = false;
      });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const fromParam = params.get(paramKeys.from);
    const toParam = params.get(paramKeys.to);
    const period = params.get(paramKeys.period);

    const labelToDateRange = labelsToDateRanges();
    let didHydrateFromUrl = false;

    if (fromParam && toParam) {
      // Frozen URL: absolute window wins, tab is "custom".
      const from = new Date(fromParam);
      const to = new Date(toParam);
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        setTab("custom");
        setDateRange({ from, to });
        didHydrateFromUrl = true;
      }
    } else if (period && labelToDateRange[period]) {
      // Live URL: recompute the preset relative to the recipient's "now".
      setTab(period);
      setDateRange({ ...labelToDateRange[period] });
      didHydrateFromUrl = true;
    }

    if (didHydrateFromUrl) {
      // Guard against circular updates until the render cycle completes.
      isHydratingRef.current = true;
      requestAnimationFrame(() => {
        isHydratingRef.current = false;
      });
      return;
    }

    // No URL params, and no default override was seeded (either this page has
    // no override, or the user already picked a window this session). If the
    // atoms are non-default (e.g. the user picked a window on a sibling page
    // that shares the same atom track), push that state into the URL so this
    // page produces a shareable link.
    const currentTab = store.get(isSourceRecent ? recentTimeTabAtom : tabAtom);
    if (currentTab !== defaultTab) {
      if (currentTab === "custom") {
        const currentDateRange = store.get(
          isSourceRecent ? recentDateRangeAtom : dateRangeAtom,
        );
        if (currentDateRange?.from && currentDateRange?.to) {
          updateDateRangeUrl("custom", currentDateRange);
        }
      } else {
        updateDateRangeUrl(currentTab);
      }
    }
    // Only run on mount. window.location.search and store.get() are
    // non-reactive reads; there's no dependency to track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { updateDateRangeUrl };
}
